import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  VideoRenderDto,
  VideoRenderStatus,
  VideoRenderUpscaleQuality,
} from "@repo/api/video-render";
import { useEffect, useRef, useState } from "react";
import { downloadFile } from "@/lib/utils";
import Toast from "@/lib/components/Toast";

const RENDER_DURATION_MULTIPLIER = 0.5;

const UPSCALE_DURATION_MULTIPLIER: Record<VideoRenderUpscaleQuality, number> = {
  "1080p": 2,
  "4k": 8,
};

export function estimateRenderTime(
  videoDurationMs: number,
  quality: VideoRenderUpscaleQuality | null,
): number {
  const upscaleMultiplier =
    quality === null ? 0 : UPSCALE_DURATION_MULTIPLIER[quality];
  const estimatedMs =
    videoDurationMs * (RENDER_DURATION_MULTIPLIER + upscaleMultiplier);

  const minDuration = quality === null ? 1 : 2;

  return Math.max(minDuration, Math.round(estimatedMs / 60000));
}

function downloadVideoRender(
  videoRender: VideoRenderDto | undefined,
  setVideoRendersToDownload: React.Dispatch<React.SetStateAction<Set<string>>>,
) {
  if (!videoRender || videoRender.status !== "done" || !videoRender.downloadUrl)
    return;

  try {
    downloadFile(videoRender.downloadUrl);
  } catch (error) {
    console.error(error);
    Toast.error("Failed to download video");
  } finally {
    setVideoRendersToDownload((prevState) => {
      const newState = new Set(prevState);
      newState.delete(videoRender.externalId);
      return newState;
    });
  }
}

export function useVideoRendersFailedWatcher(
  videoRenders: VideoRenderDto[] | undefined,
) {
  const refreshCreditBalance = useEditorStore(
    (state) => state.service.refreshCreditBalance,
  );
  const previousRenderStatuses = useRef<Map<string, VideoRenderStatus>>(
    new Map(),
  );

  useEffect(() => {
    if (!videoRenders) return;

    let hasNewFailure = false;

    for (const render of videoRenders) {
      const previousStatus = previousRenderStatuses.current.get(
        render.externalId,
      );

      if (
        previousStatus &&
        previousStatus !== "failed" &&
        render.status === "failed"
      ) {
        hasNewFailure = true;
      }

      previousRenderStatuses.current.set(render.externalId, render.status);
    }

    if (hasNewFailure) {
      refreshCreditBalance();
    }
  }, [videoRenders, refreshCreditBalance]);
}

export function useVideoRendersDownloadWatcher(
  videoRenders: VideoRenderDto[] | undefined,
) {
  const [videoRendersToDownload, setVideoRendersToDownload] = useState<
    Set<string>
  >(new Set());

  useEffect(() => {
    if (!videoRenders) return;

    for (const videoRenderId of Array.from(videoRendersToDownload)) {
      const videoRender = videoRenders.find(
        (render) => render.externalId === videoRenderId,
      );

      downloadVideoRender(videoRender, setVideoRendersToDownload);
    }
  }, [videoRenders, videoRendersToDownload]);

  return {
    setVideoRendersToDownload,
  };
}
